import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, ClipboardCheck, Calendar, History, UserCheck, Scale, BarChart3, Award, MapPin, Clock, ShieldCheck, TrendingDown, FileSearch } from 'lucide-react';
const capabilities = [{
  icon: Calendar,
  name: 'Prospective Review',
  description: 'Pre-authorization of proposed treatments before care is delivered, ensuring medical necessity from day one.'
}, {
  icon: ClipboardCheck,
  name: 'Concurrent Review',
  description: 'Real-time review of ongoing care, hospital stays, and extended treatment plans to keep claims on track.'
}, {
  icon: History,
  name: 'Retrospective Review',
  description: 'Post-service review to validate medical necessity, identify overutilization, and recover unnecessary spend.'
}, {
  icon: UserCheck,
  name: 'Peer-to-Peer Review',
  description: 'Board-certified physician reviewers conduct collegial consultations with treating providers.'
}, {
  icon: Scale,
  name: 'Appeals Management',
  description: 'Structured appeals process aligned with jurisdictional requirements, including IMR and IBR support.'
}, {
  icon: FileSearch,
  name: 'Evidence-Based Guidelines',
  description: 'Reviews aligned with ODG, MTUS, ACOEM, and state-specific treatment guidelines.'
}, {
  icon: MapPin,
  name: 'Multi-State Compliance',
  description: 'Jurisdictional expertise across all 50 states, with localized workflows for California, Texas, Florida, and beyond.'
}, {
  icon: BarChart3,
  name: 'Reporting & Analytics',
  description: 'Real-time dashboards showing review volumes, certification rates, savings, and provider performance.'
}];
const benefits = [{
  icon: TrendingDown,
  title: 'Lower medical spend',
  stat: '28%',
  description: 'Average reduction in unnecessary medical treatment through evidence-based determinations.'
}, {
  icon: Clock,
  title: 'Faster determinations',
  stat: '< 5 days',
  description: 'Most determinations issued well within jurisdictional timelines — many within 24 hours.'
}, {
  icon: Award,
  title: 'URAC accredited',
  stat: 'URAC',
  description: 'Our program is independently accredited and adheres to the strictest national standards.'
}, {
  icon: ShieldCheck,
  title: 'Compliance assurance',
  stat: '100%',
  description: 'Every review is documented, defensible, and aligned with state and federal regulations.'
}];
export function UtilizationReview() {
  return <div className="bg-brand-paper" data-id="element-933">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-934">
        <div className="absolute inset-0 opacity-10" data-id="element-935">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-936"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-937"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-938">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-939">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-940">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-941">
                Managed Care Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-942">
              <span className="text-brand-gold italic" data-id="element-943">Utilization</span> Review
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-944">
              Evidence-based medical necessity determinations that ensure
              injured workers get the right care — and your program avoids the
              wrong costs.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-945">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-946">
                Request a Consultation <ArrowRight className="w-5 h-5 ml-2" data-id="element-947" />
              </Link>
              <a href="tel:8552601053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-948">
                <Phone className="w-5 h-5 mr-2" data-id="element-949" /> 855.260.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-id="element-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-951">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-952">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform -rotate-2" data-id="element-953"></div>
            <img src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Physician reviewing a patient's medical chart" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-954" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-955">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-956">
              The Problem We Solve
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-957">
              The right care, at the right time — backed by evidence.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-958">
              Without rigorous utilization review, claims drift. Treatments
              stack up that don't meet evidence-based standards. Costs balloon.
              Injured workers can even be harmed by unnecessary procedures.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-959">
              Our URAC-accredited program uses physician reviewers and the
              latest evidence-based guidelines to make defensible, timely
              determinations — protecting both your bottom line and the worker's
              recovery path.
            </p>
            <ul className="space-y-4" data-id="element-960">
              {['URAC-accredited workflows and clinical processes', 'Board-certified physician reviewers across every specialty', 'Jurisdictional expertise in all 50 states', 'Defensible documentation for every determination'].map(item => <li key={item} className="flex items-start" data-id="element-961">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-962" />
                  <span className="text-gray-800 font-medium" data-id="element-963">{item}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-brand-paper" data-id="element-964">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-965">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-966">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-967">
              What's Included
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-968">
              A complete UR program.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-969">
              From pre-authorization through appeals — every step of the
              utilization review process, under one roof.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-970">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-971">
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-972">
                    <Icon className="w-6 h-6" data-id="element-973" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-974">
                    {cap.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-975">
                    {cap.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-id="element-976">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-977">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-978">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-979">
              The Arissa Difference
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-980">
              Measurable outcomes you can defend.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-981">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-982">
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-983"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-984" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-985">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-986">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-987">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-988">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-989"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-990">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-991">
            Bring rigor to every review.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-992">
            Talk to our team about how Arissa's UR program can improve outcomes
            and drive measurable savings across your book of business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-993">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-994">
              Schedule a Walkthrough
            </Link>
            <a href="tel:8552601053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-995">
              <Phone className="w-5 h-5 mr-2" data-id="element-996" /> 855.260.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}